import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { receiptFormatApi } from "@/lib/api";
import { toast } from "sonner";
import { 
  FileImage, 
  Save, 
  Trash2, 
  Upload, 
  Building2, 
  Receipt,
  FileText,
  Eye,
  Loader2
} from "lucide-react";

export default function ReceiptFormat() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState({
    lab_name: "",
    lab_address: "",
    lab_phone: "",
    lab_email: "",
    lab_rfc: "",
    lab_website: "",
    logo_header: null,
    footer_text: "",
    show_terms: true,
    terms_text: ""
  });
  
  const headerLogoRef = useRef(null);

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const response = await receiptFormatApi.get();
      setConfig(prev => ({ ...prev, ...response.data }));
    } catch (error) {
      console.error("Error fetching config:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setConfig(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await receiptFormatApi.save(config);
      toast.success("Configuración de recibo guardada exitosamente");
    } catch (error) {
      toast.error("Error al guardar la configuración");
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      toast.error("Por favor seleccione una imagen válida");
      return;
    }

    // Validate file size (max 20MB)
    if (file.size > 20 * 1024 * 1024) {
      toast.error("La imagen no debe superar 20MB");
      return;
    }

    // Convert to base64
    const reader = new FileReader();
    reader.onload = async (e) => {
      const base64 = e.target.result;
      const newConfig = { ...config, logo_header: base64 };
      setConfig(newConfig);
      
      // Auto-save after uploading logo
      try {
        await receiptFormatApi.save(newConfig);
        toast.success("Logo guardado exitosamente");
      } catch (error) {
        toast.error("Error al guardar el logo");
        console.error("Error saving logo:", error);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleDeleteLogo = async () => {
    const newConfig = { ...config, logo_header: null };
    setConfig(newConfig);
    
    // Auto-save after deleting logo
    try {
      await receiptFormatApi.save(newConfig);
      toast.success("Logo eliminado");
    } catch (error) {
      toast.error("Error al eliminar el logo");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Receipt className="h-6 w-6" />
            Formato de Recibo de Pago
          </h1>
          <p className="text-muted-foreground">
            Configure el diseño de los recibos de pago con los datos de su laboratorio
          </p>
        </div>
        <Button onClick={handleSave} disabled={saving}>
          {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
          Guardar Configuración
        </Button>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Información del Laboratorio */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              Datos del Laboratorio
            </CardTitle>
            <CardDescription>
              Información que aparecerá en el encabezado de los recibos
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="lab_name">Nombre del Laboratorio</Label>
              <Input
                id="lab_name"
                value={config.lab_name}
                onChange={(e) => handleChange("lab_name", e.target.value)}
                placeholder="LABORATORIO CLÍNICO S.A. DE C.V."
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="lab_rfc">RFC</Label>
              <Input
                id="lab_rfc"
                value={config.lab_rfc || ""}
                onChange={(e) => handleChange("lab_rfc", e.target.value)}
                placeholder="LAB123456ABC"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="lab_address">Dirección</Label>
              <Textarea
                id="lab_address"
                value={config.lab_address || ""}
                onChange={(e) => handleChange("lab_address", e.target.value)}
                placeholder="Av. Principal #123, Col. Centro, C.P. 12345, Ciudad, Estado"
                rows={2}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="lab_phone">Teléfono</Label>
                <Input
                  id="lab_phone"
                  value={config.lab_phone || ""}
                  onChange={(e) => handleChange("lab_phone", e.target.value)}
                  placeholder="(123) 456-7890"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lab_email">Email</Label>
                <Input
                  id="lab_email"
                  type="email"
                  value={config.lab_email || ""}
                  onChange={(e) => handleChange("lab_email", e.target.value)}
                  placeholder="contacto@laboratorio.com"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="lab_website">Sitio Web</Label>
              <Input
                id="lab_website"
                value={config.lab_website || ""}
                onChange={(e) => handleChange("lab_website", e.target.value)}
                placeholder="www.laboratorio.com"
              />
            </div>
          </CardContent>
        </Card>

        {/* Logotipo */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileImage className="h-5 w-5" />
              Logotipo del Recibo
            </CardTitle>
            <CardDescription>
              Suba el logotipo que aparecerá en el encabezado del recibo (PNG, JPG - máx 20MB)
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Logo de Encabezado */}
            <div className="space-y-3">
              <Label>Logo de Encabezado</Label>
              <div className="flex items-start gap-4">
                <div className="w-40 h-28 border-2 border-dashed rounded-lg flex items-center justify-center bg-muted/30 overflow-hidden">
                  {config.logo_header ? (
                    <img 
                      src={config.logo_header} 
                      alt="Logo encabezado" 
                      className="max-w-full max-h-full object-contain"
                    />
                  ) : (
                    <FileImage className="h-10 w-10 text-muted-foreground" />
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  <input
                    type="file"
                    ref={headerLogoRef}
                    accept="image/*"
                    onChange={handleLogoUpload}
                    className="hidden"
                  />
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => headerLogoRef.current?.click()}
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    Subir Logo
                  </Button>
                  {config.logo_header && (
                    <Button 
                      variant="destructive" 
                      size="sm"
                      onClick={handleDeleteLogo}
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Eliminar
                    </Button>
                  )}
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                Recomendado: imagen horizontal con fondo transparente
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Pie de Página */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Pie de Página del Recibo
            </CardTitle>
            <CardDescription>
              Texto adicional que aparecerá al final de cada recibo
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="footer_text">Texto de Pie de Página</Label>
              <Textarea
                id="footer_text"
                value={config.footer_text || ""}
                onChange={(e) => handleChange("footer_text", e.target.value)}
                placeholder="Gracias por su preferencia. Este recibo no es comprobante fiscal."
                rows={2}
              />
            </div>

            <Separator />

            <div className="space-y-2">
              <Label htmlFor="terms_text">Términos y Condiciones (opcional)</Label>
              <Textarea
                id="terms_text"
                value={config.terms_text || ""}
                onChange={(e) => handleChange("terms_text", e.target.value)}
                placeholder="Los resultados estarán disponibles en 24-48 horas. No se realizan devoluciones una vez tomada la muestra."
                rows={3}
              />
              <p className="text-xs text-muted-foreground">
                Estos términos aparecerán en la parte inferior del recibo
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Preview Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Eye className="h-5 w-5" />
            Vista Previa del Recibo
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="border rounded-lg p-6 bg-white max-w-md mx-auto">
            {/* Header */}
            <div className="text-center border-b pb-4 mb-4">
              {config.logo_header ? (
                <img 
                  src={config.logo_header} 
                  alt="Logo" 
                  className="h-16 mx-auto mb-2 object-contain"
                />
              ) : (
                <h2 className="text-lg font-bold">{config.lab_name || "LABORATORIO CLÍNICO"}</h2>
              )}
              {config.logo_header && config.lab_name && (
                <h2 className="text-sm font-semibold">{config.lab_name}</h2>
              )}
              {config.lab_rfc && <p className="text-xs text-gray-500">RFC: {config.lab_rfc}</p>}
              {config.lab_address && <p className="text-xs text-gray-500">{config.lab_address}</p>}
              <p className="text-xs text-gray-500">
                {[config.lab_phone, config.lab_email].filter(Boolean).join(" | ")}
              </p>
              {config.lab_website && <p className="text-xs text-gray-500">{config.lab_website}</p>}
            </div>

            {/* Content placeholder */}
            <div className="space-y-2 mb-4">
              <div className="flex justify-between text-sm">
                <span className="font-medium">Recibo No:</span>
                <span>REC-001234</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="font-medium">Fecha:</span>
                <span>{new Date().toLocaleDateString()}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="font-medium">Paciente:</span>
                <span>Juan Pérez</span>
              </div>
              <Separator className="my-2" />
              <div className="flex justify-between text-sm">
                <span>Biometría Hemática</span>
                <span>$250.00</span>
              </div>
              <div className="flex justify-between text-sm">
                <span>Química Sanguínea</span>
                <span>$350.00</span>
              </div>
              <Separator className="my-2" />
              <div className="flex justify-between text-sm font-bold">
                <span>TOTAL:</span>
                <span>$600.00</span>
              </div>
            </div>

            {/* Footer */}
            {(config.footer_text || config.terms_text) && (
              <div className="border-t pt-3 mt-4">
                {config.footer_text && (
                  <p className="text-xs text-gray-500 text-center mb-2">{config.footer_text}</p>
                )}
                {config.terms_text && (
                  <p className="text-[10px] text-gray-400 text-center">{config.terms_text}</p>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Alert>
        <AlertDescription>
          <strong>Nota:</strong> Los cambios se aplicarán a todos los nuevos recibos que se generen. 
          Los recibos ya generados no se verán afectados.
        </AlertDescription>
      </Alert>
    </div>
  );
}
